import React, {  FC, useEffect, useRef, useState } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import {
  Supersource,
  FileInfo,
  ModuleInstance,
  SubsourceInfo,
  CarryoverModuleInstance,
  ModuleInstancesMap,
} from './types';
import ModuleInstanceRow from './ModuleInstanceRow';

interface AddEditLessonProps {
  supersources: Supersource[];
  studentId: string;
  isNew: boolean;
  userFiles: FileInfo[];
  defaultSupersourceId?: number;
  moduleInstances: ModuleInstance[];
  defaultSubsourcesData: SubsourceInfo[];
  carryoverModuleInstances: CarryoverModuleInstance[];
  overrideFormName?: string;
}

const queryClient = new QueryClient();

const AddEditLesson: FC<AddEditLessonProps> = (props) => {
  const [
    moduleInstancesMap,
    setModuleInstancesMap,
  ] = useState<ModuleInstancesMap>({});
  const [curFormIndex, setCurFormIndex] = useState(0);

  // set up module instances map
  useEffect(() => {
    const populatedModuleInstancesMap = { ...moduleInstancesMap };
    for (const moduleInstance of props.moduleInstances) {
      populatedModuleInstancesMap[moduleInstance.id] = moduleInstance;
    }
    if (props.isNew && Object.keys(populatedModuleInstancesMap).length === 0) {
      props.defaultSubsourcesData.forEach(
        ({ subsourceId, supersourceId }, index) => {
          populatedModuleInstancesMap[index] = {
            subsourceId,
            supersourceId,
            position: index,
            customFileIds: [],
          };
        },
      );
      const moduleInstancesLen = Object.keys(populatedModuleInstancesMap).length;
      props.carryoverModuleInstances.forEach((moduleInstance, index) => {
        populatedModuleInstancesMap[moduleInstancesLen + index] = {
          position: moduleInstancesLen + index,
          customFileIds: moduleInstance.customFileIds || [],
          customContent: moduleInstance.customContent,
          moduleId: moduleInstance.moduleId,
          subsourceId: moduleInstance.subsourceId,
          supersourceId: moduleInstance.supersourceId,
        };
      });
    }
    setModuleInstancesMap(populatedModuleInstancesMap);
    setCurFormIndex(Object.keys(populatedModuleInstancesMap).length);
  }, [
    props.moduleInstances,
    props.defaultSubsourcesData,
    props.isNew,
    props.carryoverModuleInstances,
  ]);

  // helper to set the next state of the module at index
  const updateModuleInstance = <T extends keyof ModuleInstance>(
    index: string,
    key: T,
    newVal: ModuleInstance[T],
    options?: {
      clearKeys?: T[], // clearKeys will null out the keys passed in
    },
  ) => {
    const nextModuleInstancesMap = { ...moduleInstancesMap };
    nextModuleInstancesMap[index] = {
      ...moduleInstancesMap[index],
    };
    nextModuleInstancesMap[index][key] = newVal;
    if (options?.clearKeys) {
      for (const clearKey of options.clearKeys) {
        nextModuleInstancesMap[index][clearKey] = null;
      }
    }
    setModuleInstancesMap(nextModuleInstancesMap);
  };

  const getSortedFormIndices = () => {
    const formIndices = Object.keys(moduleInstancesMap);
    formIndices.sort((a, b) => moduleInstancesMap[a].position - moduleInstancesMap[b].position);
    return formIndices;
  };

  const onDeleteModuleInstance = (formIndex: string) => {
    const newModuleInstancesMap = { ...moduleInstancesMap };
    delete newModuleInstancesMap[formIndex];
    setModuleInstancesMap(newModuleInstancesMap);
  };

  const onUpdateCustomFile = (formIndex: string, fileIndex: number, customFileId: number) => {
    const instance = moduleInstancesMap[formIndex];
    const customFileIds = [...instance.customFileIds];
    customFileIds[fileIndex] = customFileId;
    updateModuleInstance(formIndex, 'customFileIds', customFileIds);
  };

  const onAddCustomFile = (formIndex: string) => {
    const instance = moduleInstancesMap[formIndex];
    const customFileIds = [...instance.customFileIds];
    customFileIds.push(null);
    updateModuleInstance(formIndex, 'customFileIds', customFileIds);
  };

  const onDeleteCustomFile = (formIndex: string, fileIndex: number) => {
    const instance = moduleInstancesMap[formIndex];
    const customFileIds = [...instance.customFileIds];
    customFileIds.splice(fileIndex, 1);
    updateModuleInstance(formIndex, 'customFileIds', customFileIds);
  };

  const onAddModuleInstance = (prepend = false) => {
    const newModuleInstancesMap = { ...moduleInstancesMap };
    const positions = Object.values(moduleInstancesMap).map(mod => mod.position);
    positions.push(-1); // just in case this is the first item, make sure a value exists
    newModuleInstancesMap[curFormIndex] = {
      position: prepend ? Math.min(...positions) - 1 : Math.max(...positions) + 1,
      customFileIds: [],
      supersourceId: props.defaultSupersourceId,
    };
    setModuleInstancesMap(newModuleInstancesMap);
    setCurFormIndex(curFormIndex + 1);
  };

  const onSlideUpModuleInstance = (formIndex: string) => {
    slideModuleInstance(formIndex, true);
  };

  const onSlideDownModuleInstance = (formIndex: string) => {
    slideModuleInstance(formIndex, false);
  };

  const slideModuleInstance = (formIndex: string, up = true) => {
    const formIndices = getSortedFormIndices();
    const pos = formIndices.indexOf(formIndex);
    const direction = up ? -1 : 1;
    const nextPos = pos + direction;
    if (nextPos < 0 || nextPos >= formIndices.length) return;
    const toSwapFormIndex = formIndices[nextPos];
    const newModuleInstancesMap = { ...moduleInstancesMap };
    // swap this item position with the item above/below it
    const curItemPosition = newModuleInstancesMap[formIndex].position;
    const toSwapItemPosition = newModuleInstancesMap[toSwapFormIndex].position;
    newModuleInstancesMap[formIndex] = {
      ...newModuleInstancesMap[formIndex],
      position: toSwapItemPosition,
    };
    newModuleInstancesMap[toSwapFormIndex] = {
      ...newModuleInstancesMap[toSwapFormIndex],
      position: curItemPosition,
    };
    setModuleInstancesMap(newModuleInstancesMap);
  };

  ///// rendering logic /////

  const formIndices = getSortedFormIndices();
  const includeModuleIds = Object.values(moduleInstancesMap).map(({ moduleId }) => moduleId).filter(x => x);

  return (
    <QueryClientProvider client={queryClient}>
      <div className="row">
        <div className="col-12">
          <button onClick={() => onAddModuleInstance(true)} type="button" className="btn btn-info">
            <i className="fa fa-plus"></i> Add module
          </button>
        </div>
      </div>
      {formIndices.map((formIndex, i) => {
        const moduleInstance = moduleInstancesMap[formIndex];
        return (
          <div key={formIndex}>
            <ModuleInstanceRow
              formIndex={formIndex}
              moduleInstance={moduleInstance}
              studentId={props.studentId}
              supersources={props.supersources}
              includeModuleIds={includeModuleIds}
              userFiles={props.userFiles}
              canSlideUp={i > 0}
              canSlideDown={i < formIndices.length - 1}
              onChangeSupersourceId={supersourceId => updateModuleInstance(formIndex, 'supersourceId', supersourceId, {
                clearKeys: ['moduleId', 'subsourceId'],
              })}
              onChangeSubsourceId={subsourceId => updateModuleInstance(formIndex, 'subsourceId', subsourceId, {
                clearKeys: ['moduleId'],
              })}
              onChangeModuleId={moduleId => updateModuleInstance(formIndex, 'moduleId', moduleId)}
              onSlideUp={() => onSlideUpModuleInstance(formIndex)}
              onSlideDown={() => onSlideDownModuleInstance(formIndex)}
              onDelete={() => onDeleteModuleInstance(formIndex)}
              onAddCustomFile={() => onAddCustomFile(formIndex)}
              onUpdateCustomContent={content => updateModuleInstance(formIndex, 'customContent', content)}
              onDeleteCustomFile={fileIndex => onDeleteCustomFile(formIndex, fileIndex)}
              onUpdateCustomFile={(fileIndex, customFileId) => onUpdateCustomFile(formIndex, fileIndex, customFileId)}
              overrideFormName={props.overrideFormName}
            />
          </div>
        );
      })}
      <div className="row border-top">
        <div className="col-12">
          <button onClick={() => onAddModuleInstance(false)} type="button" className="btn btn-info">
            <i className="fa fa-plus"></i> Add module
          </button>
        </div>
      </div>
    </QueryClientProvider>
  );
};

export default AddEditLesson;
